import { useEffect, useState } from 'react';
import {
  Loader2,
  BarChart3,
  TrendingUp,
  PieChart as PieIcon,
  Users,
  FileText,
  Tag,
  Sparkles,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { apiClient } from '@/lib/api';

interface AnalyticsData {
  days: number;
  by_status: { status: string; count: number }[];
  by_day: { day: string; count: number }[];
  by_source: { source: string; count: number }[];
  by_assignee: { assignee_id: string; assignee_name: string; count: number }[];
  by_form: { form_id: string; form_name: string; form_slug: string; count: number }[];
  by_type: { type_id: string; type_name: string; type_color: string | null; count: number }[];
  by_skill: { skill_id: string; skill_name: string; count: number }[];
}

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Programmato',
  confirmed: 'Confermato',
  cancelled: 'Annullato',
  completed: 'Completato',
  no_show: 'Non presentato',
};
const STATUS_COLORS: Record<string, string> = {
  scheduled: '#f59e0b',
  confirmed: '#10b981',
  cancelled: '#ef4444',
  completed: '#6366f1',
  no_show: '#9ca3af',
};
const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manuali',
  public_form: 'Da form pubblico',
};
const SOURCE_COLORS = ['#6366f1', '#3b82f6'];
const FALLBACK_PALETTE = ['#6366f1', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#14b8a6', '#ec4899', '#f97316', '#06b6d4'];

export default function CalendarAnalyticsTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<{
        days: number;
        by_status: { status: string; count: number }[];
        by_day: { day: string; count: number }[];
        by_source: { source: string; count: number }[];
        by_assignee: { assignee_id: string; assignee_name: string; count: number }[];
        by_form: { form_id: string; form_name: string; form_slug: string; count: number }[];
        by_type: { type_id: string; type_name: string; type_color: string | null; count: number }[];
        by_skill: { skill_id: string; skill_name: string; count: number }[];
      }>(`/calendar/analytics/overview?days=${days}`)
      .then((res) => {
        if (!cancelled && res?.success && res.data) setData(res.data);
      })
      .catch(e => console.error('analytics load failed', e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const statusData = (data?.by_status || []).map(r => ({
    name: STATUS_LABELS[r.status] || r.status,
    value: r.count,
    color: STATUS_COLORS[r.status] || '#6366f1',
  }));
  const sourceData = (data?.by_source || []).map(r => ({
    name: SOURCE_LABELS[r.source] || r.source,
    value: r.count,
  }));
  const dayData = (data?.by_day || []).map(r => ({ day: r.day.slice(5), count: r.count }));

  const assigneeData = (data?.by_assignee || []).map(r => ({
    name: r.assignee_name,
    value: r.count,
  }));
  const formData = (data?.by_form || []).map(r => ({
    name: r.form_name,
    value: r.count,
  }));
  const typeData = (data?.by_type || []).map((r, i) => ({
    name: r.type_name,
    value: r.count,
    color: r.type_color || FALLBACK_PALETTE[i % FALLBACK_PALETTE.length],
  }));
  const skillData = (data?.by_skill || []).map(r => ({
    name: r.skill_name,
    value: r.count,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Analytics Calendario</h2>
          <p className="text-sm text-muted-foreground">Statistiche aggregate sugli appuntamenti</p>
        </div>
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Ultimi 7 giorni</SelectItem>
            <SelectItem value="30">Ultimi 30 giorni</SelectItem>
            <SelectItem value="90">Ultimi 90 giorni</SelectItem>
            <SelectItem value="365">Ultimo anno</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-500" />
                Appuntamenti per giorno
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dayData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-500" />
                Distribuzione per stato
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={statusData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value">
                    {statusData.map((s, i) => (
                      <Cell key={i} fill={s.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieIcon className="w-5 h-5 text-purple-500" />
                Origine appuntamenti
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={sourceData} dataKey="value" nameKey="name" outerRadius={90} label>
                    {sourceData.map((_, i) => (
                      <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Task #475: breakdown per risorsa */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                Top risorse per appuntamenti
              </CardTitle>
            </CardHeader>
            <CardContent>
              {assigneeData.length === 0 ? (
                <EmptyChart label="Nessun dato disponibile per le risorse" />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, assigneeData.length * 32)}>
                  <BarChart data={assigneeData} layout="vertical" margin={{ left: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Task #475: breakdown per form di provenienza */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                Top form di provenienza
              </CardTitle>
            </CardHeader>
            <CardContent>
              {formData.length === 0 ? (
                <EmptyChart label="Nessuna submission da form pubblici nel periodo" />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, formData.length * 32)}>
                  <BarChart data={formData} layout="vertical" margin={{ left: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Task #475: breakdown per tipo appuntamento */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-purple-500" />
                Distribuzione per tipo
              </CardTitle>
            </CardHeader>
            <CardContent>
              {typeData.length === 0 ? (
                <EmptyChart label="Nessun tipo di appuntamento utilizzato nel periodo" />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={typeData} dataKey="value" nameKey="name" outerRadius={90} label>
                      {typeData.map((t, i) => (
                        <Cell key={i} fill={t.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Task #475: breakdown per skill (proxy: skill possedute dagli assegnatari) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Skill più sfruttate
              </CardTitle>
            </CardHeader>
            <CardContent>
              {skillData.length === 0 ? (
                <EmptyChart label="Nessuna skill mappata sulle risorse assegnate" />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, skillData.length * 32)}>
                  <BarChart data={skillData} layout="vertical" margin={{ left: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function EmptyChart({ label }: { label: string }) {
  return (
    <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground text-center px-4">
      {label}
    </div>
  );
}
